import type { BucketDocument, PageReviewRow } from '@oysterjs/smartfill-contracts';
import { useState } from 'react';
import { usePaste } from '../sidebar/paste';
import { useSidebarSession } from '../bridge/session';
import {
  entityDisplayLabel,
  humanizeName,
  repeatedListFor,
  repeatedListsIn,
  type RepeatedList
} from '../buckets/document';
import { pluralize } from '../sidebar/presentation';

const UNBOUND_REPEATED_SOURCE = 'UNBOUND_REPEATED_SOURCE';

/**
 * Offers the sibling records behind fields the resolver refused to fill
 * because it could not tell which repeated record (which driver, which
 * vehicle) the page wanted. Choosing one re-pastes scoped to that record,
 * the same path as the browser's section paste.
 */
export function EntityPicker({
  rows,
  document
}: {
  rows: PageReviewRow[];
  document: BucketDocument | undefined;
}) {
  const paste = usePaste();
  const { snapshot } = useSidebarSession();
  const [pastingEntityId, setPastingEntityId] = useState<string | null>(null);

  const unbound = rows.filter((row) => row.reason === UNBOUND_REPEATED_SOURCE);
  const unmatched = rows.filter((row) => row.reason === 'NO_MATCH');
  if (
    !document ||
    (unbound.length === 0 &&
      (unmatched.length === 0 || snapshot.pageSession.selection?.scopeEntityId))
  )
    return null;
  const lists = listsFor(document, unbound.length ? unbound : unmatched);
  if (lists.length === 0) return null;

  const choose = async (entityId: string, label: string) => {
    setPastingEntityId(entityId);
    try {
      await paste.pasteOnce({ scopeEntityId: entityId, scopeLabel: label });
    } finally {
      setPastingEntityId(null);
    }
  };

  return (
    <section className="entity-picker" aria-labelledby="entity-picker-heading">
      <div className="section-heading">
        <h2 id="entity-picker-heading">
          {unbound.length ? question(lists) : 'Fill from a specific record'}
        </h2>
      </div>
      <p className="entity-picker__description">
        {unbound.length
          ? `${unbound.length} ${pluralize(unbound.length, 'field belongs', 'fields belong')} to one of several records. Pick the one this page is about so Cara can fill it.`
          : 'If these fields are about one driver or vehicle, choose it here. Cara will remember your choice on following pages.'}
      </p>
      {lists.map((list) => (
        <div
          key={list.key}
          className="entity-picker__options"
          role="group"
          aria-label={`Choose from ${humanizeName(list.relation).toLowerCase()}`}
        >
          {list.entities.map(({ entity, ordinal }) => {
            const label = entityDisplayLabel(entity, ordinal);
            return (
              <button
                key={entity.id}
                type="button"
                className="button button--quiet entity-picker__option"
                aria-busy={pastingEntityId === entity.id}
                disabled={pastingEntityId !== null}
                onClick={() => void choose(entity.id, label)}
              >
                {label}
              </button>
            );
          })}
        </div>
      ))}
    </section>
  );
}

/**
 * The lists behind the refused rows. A derived composite (full name, one-line
 * address) has no property in the document, so when no row resolves to a
 * list the picker falls back to every repeated list rather than hiding.
 */
function listsFor(document: BucketDocument, unbound: PageReviewRow[]): RepeatedList[] {
  const byKey = new Map<string, RepeatedList>();
  for (const row of unbound) {
    const list = row.propertyId ? repeatedListFor(document, row.propertyId) : null;
    if (list) byKey.set(list.key, list);
  }
  if (byKey.size > 0) return Array.from(byKey.values());
  return repeatedListsIn(document);
}

function question(lists: RepeatedList[]): string {
  if (lists.length === 1) return `Choose from ${humanizeName(lists[0].relation).toLowerCase()}`;
  return 'Which record?';
}
